import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Dialog } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import SubdirectoryArrowLeftRoundedIcon from "@mui/icons-material/SubdirectoryArrowLeftRounded";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import NorthEastRoundedIcon from "@mui/icons-material/NorthEastRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";

import { selectRoles } from "../../features/roleSlice";
import { selectUser } from "../../features/userSlice";
import { searchableNavPages, matchesQuery } from "./SideNav";

/* ------------------------------------------------------------------
   Quick-jump palette — ⌘K / Ctrl+K from any page, or the search pill in
   the header. Same pages, keywords and role filtering as the drawer search
   (both read NAV_SECTIONS). When nothing
   matches, the last row searches the Help & FAQ for the same words.
------------------------------------------------------------------ */

const MAX_RESULTS = 9;

// Shown before the user types anything (filtered to what they can open).
const SUGGESTED = [
  "/forms",
  "/forms/my-submissions",
  "/messages",
  "/windowdoor/lead-times",
  "/help",
];

// Same key QuickAccess saves homepage favorites under.
const readFavorites = (user) => {
  try {
    const parsed = JSON.parse(
      localStorage.getItem(`homeFavorites:${user?.id || "anon"}`) || "[]"
    );
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export const isMac = () =>
  typeof navigator !== "undefined" &&
  /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent || "");

const CommandPalette = ({ open, onClose }) => {
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  const role = useSelector(selectRoles);
  const userRoles = useMemo(() => role?.roles || [], [role]);
  const grantedPaths = role?.grantedPaths;
  const pages = useMemo(
    () => searchableNavPages(userRoles, grantedPaths),
    [userRoles, grantedPaths]
  );

  const [query, setQuery] = useState("");
  const [highlighted, setHighlighted] = useState(0);
  const listRef = useRef(null);

  // Fresh state every time it opens.
  useEffect(() => {
    if (open) {
      setQuery("");
      setHighlighted(0);
    }
  }, [open]);

  const trimmed = query.trim();

  const rows = useMemo(() => {
    const byPath = new Map(pages.map((p) => [p.path, p]));
    if (!trimmed) {
      const favorites = readFavorites(user)
        .map((path) => byPath.get(path))
        .filter(Boolean)
        .map((p) => ({ ...p, kind: "favorite" }));
      const favPaths = new Set(favorites.map((f) => f.path));
      const suggested = SUGGESTED.map((path) => byPath.get(path))
        .filter((p) => p && !favPaths.has(p.path))
        .map((p) => ({ ...p, kind: "suggested" }));
      return [...favorites.slice(0, 6), ...suggested];
    }
    const matches = pages
      .filter((page) => matchesQuery(page.haystack, trimmed))
      .slice(0, MAX_RESULTS)
      .map((p) => ({ ...p, kind: "page" }));
    return [
      ...matches,
      {
        kind: "help",
        name: `Search Help & FAQ for “${trimmed}”`,
        path: `/help?q=${encodeURIComponent(trimmed)}`,
      },
    ];
  }, [pages, trimmed, user]);

  useEffect(() => {
    setHighlighted(0);
  }, [trimmed]);

  // Keep the highlighted row visible while arrowing through a long list.
  useEffect(() => {
    listRef.current
      ?.querySelector(`[data-row="${highlighted}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [highlighted]);

  const go = (row) => {
    if (!row) return;
    onClose();
    navigate(row.path);
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlighted((h) => Math.min(h + 1, rows.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlighted((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      go(rows[highlighted]);
    }
  };

  const heading = trimmed ? "Pages" : "Jump to";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      aria-label="Search pages"
      sx={{
        "& .MuiDialog-container": { alignItems: "flex-start" },
        "& .MuiDialog-paper": { mt: { xs: 2, sm: "12vh" }, mx: 1.5, width: "calc(100% - 24px)" },
      }}
    >
      <div className="flex items-center gap-3 border-b border-[color:var(--app-divider)] px-4 py-3">
        <SearchIcon className="app-muted shrink-0" />
        <input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Search pages…"
          aria-label="Search pages"
          className="min-w-0 flex-1 border-0 bg-transparent p-0 text-base text-[color:var(--app-text)] outline-none placeholder:text-[color:var(--app-placeholder)] focus:ring-0"
        />
        <kbd className="app-chip hidden font-sans sm:inline-flex">esc</kbd>
      </div>

      <div ref={listRef} className="max-h-[60vh] overflow-y-auto p-2">
        {rows.length > 0 && (
          <p className="app-eyebrow px-3 pb-1 pt-2">{heading}</p>
        )}
        {rows.map((row, index) => {
          const active = index === highlighted;
          const Icon =
            row.kind === "help"
              ? HelpOutlineRoundedIcon
              : row.kind === "favorite"
              ? StarRoundedIcon
              : NorthEastRoundedIcon;
          return (
            <button
              key={`${row.kind}-${row.path}`}
              type="button"
              data-row={index}
              onMouseEnter={() => setHighlighted(index)}
              onClick={() => go(row)}
              className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors ${
                active ? "bg-sky-500/15" : ""
              } ${row.kind === "help" ? "mt-1" : ""}`}
            >
              <span
                className={`app-icon-tile h-8 w-8 rounded-lg ${
                  row.kind === "favorite"
                    ? "bg-amber-500/15 text-amber-600 dark:text-amber-300"
                    : row.kind === "help"
                    ? "bg-rose-500/15 text-rose-600 dark:text-rose-300"
                    : "bg-sky-500/15 text-sky-600 dark:text-sky-300"
                }`}
              >
                <Icon sx={{ fontSize: 17 }} />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-semibold">
                  {row.name}
                </span>
                {row.section && (
                  <span className="app-muted block truncate text-xs">
                    {row.group ? `${row.section} · ${row.group}` : row.section}
                  </span>
                )}
              </span>
              {active && (
                <SubdirectoryArrowLeftRoundedIcon
                  className="app-muted shrink-0"
                  sx={{ fontSize: 16 }}
                />
              )}
            </button>
          );
        })}
      </div>

      <div className="app-muted hidden items-center gap-4 border-t border-[color:var(--app-divider)] px-4 py-2 text-[11px] sm:flex">
        <span>
          <kbd className="app-chip font-sans">↑</kbd>{" "}
          <kbd className="app-chip font-sans">↓</kbd> to move
        </span>
        <span>
          <kbd className="app-chip font-sans">enter</kbd> to open
        </span>
        <span className="ml-auto">
          <kbd className="app-chip font-sans">{isMac() ? "⌘" : "Ctrl"} K</kbd>{" "}
          from any page
        </span>
      </div>
    </Dialog>
  );
};

export default CommandPalette;
